import {
  Directive,
  ElementRef,
  HostListener,
  Renderer2,
  OnInit,
  Input
} from '@angular/core';

@Directive({
  selector: '[appDraggable]',
  standalone: true
})
export class DraggableDirective implements OnInit {
  private isDragging = false;
  private hasMoved = false;
  private dragOffset = { x: 0, y: 0 };
  private dragStartPos = { x: 0, y: 0 };
  private dragThreshold = 5;
  private elementPos = { left: 0, top: 0 };
  private hasBeenDragged = false;

  @Input() onClickCallback: (() => void) | null = null;
  @Input() defaultBottom: number = 90;
  @Input() defaultRight: number = 20;

  constructor(private el: ElementRef, private renderer: Renderer2) { }

  ngOnInit(): void {
    this.renderer.setStyle(this.el.nativeElement, 'position', 'fixed');
    this.renderer.setStyle(this.el.nativeElement, 'z-index', '1000');
    this.setDefaultPosition();

  }

  private setDefaultPosition() {
    this.renderer.setStyle(this.el.nativeElement, 'left', 'auto');
    this.renderer.setStyle(this.el.nativeElement, 'top', 'auto');
    this.renderer.setStyle(this.el.nativeElement, 'bottom', `${this.defaultBottom}px`);
    this.renderer.setStyle(this.el.nativeElement, 'right', `${this.defaultRight}px`);
    this.hasBeenDragged = false;
  }

  private setFixedPosition() {
    this.renderer.setStyle(this.el.nativeElement, 'left', `${this.elementPos.left}px`);
    this.renderer.setStyle(this.el.nativeElement, 'top', `${this.elementPos.top}px`);
    this.renderer.setStyle(this.el.nativeElement, 'right', 'auto');
    this.renderer.setStyle(this.el.nativeElement, 'bottom', 'auto');
    this.hasBeenDragged = true;
  }

  private getViewportBounds() {
    const fabWidth = this.el.nativeElement.offsetWidth;
    const fabHeight = this.el.nativeElement.offsetHeight;
    const topMenuHeight = 65; // Height of top menu
    const bottomThreshold = this.getBottomThreshold(); // For bottom tablet menu


    return {
      minLeft: 0,
      minTop: topMenuHeight,
      maxLeft: window.innerWidth - fabWidth,
      maxTop: window.innerHeight - fabHeight - bottomThreshold,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight
    };
  }

  private clampToViewport(left: number, top: number) {
    const bounds = this.getViewportBounds();
    return {
      left: Math.max(bounds.minLeft, Math.min(left, bounds.maxLeft)),
      top: Math.max(bounds.minTop, Math.min(top, bounds.maxTop))
    };
  }

  @HostListener('mousedown', ['$event'])
  @HostListener('touchstart', ['$event'])
  public startDrag(event: MouseEvent | TouchEvent): void {
    if (event instanceof MouseEvent && event.button !== 0) return;
    event.preventDefault();

    this.isDragging = true;
    this.hasMoved = false;

    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

    // Get current element position in viewport coordinates
    const rect = this.el.nativeElement.getBoundingClientRect();

    // Calculate offset from mouse to element's top-left corner
    this.dragOffset.x = clientX - rect.left;
    this.dragOffset.y = clientY - rect.top;

    // Store current viewport position
    this.elementPos.left = rect.left;
    this.elementPos.top = rect.top;

    this.dragStartPos.x = clientX;
    this.dragStartPos.y = clientY;
  }

  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  public onDrag(event: MouseEvent | TouchEvent): void {
    if (!this.isDragging) return;

    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
    // Check if we've moved beyond the threshold
    const dx = Math.abs(clientX - this.dragStartPos.x);
    const dy = Math.abs(clientY - this.dragStartPos.y);
    if (dx <= this.dragThreshold && dy <= this.dragThreshold) return;

    this.hasMoved = true;

    // Calculate new position in viewport coordinates
    const newLeft = clientX - this.dragOffset.x;
    const newTop = clientY - this.dragOffset.y;

    // Clamp to viewport bounds
    const clampedPos = this.clampToViewport(newLeft, newTop);

    this.elementPos.left = clampedPos.left;
    this.elementPos.top = clampedPos.top;

    this.setFixedPosition();
  }

  @HostListener('document:mouseup', ['$event'])
  @HostListener('document:touchend', ['$event'])
  public endDrag(event: MouseEvent | TouchEvent): void {
    if (!this.isDragging) return;
    this.isDragging = false;

    if (!this.hasMoved) {
      // Handle click
      if (typeof this.onClickCallback === 'function') this.onClickCallback();
    }
  }

  @HostListener('window:resize')
  public onResize(): void {

    if (this.hasBeenDragged) {
      // Ensure it stays within bounds after resize
      const clampedPos = this.clampToViewport(this.elementPos.left, this.elementPos.top);
      this.elementPos.left = clampedPos.left;
      this.elementPos.top = clampedPos.top;

      this.setFixedPosition();
    } else {
      this.setDefaultPosition();
    }
  }

  private getAndroidMajorVersion(): number | null {
    const match = navigator.userAgent.match(/Android\s(\d+)(?:[._]\d+)?/i);
    if (!match?.[1]) return null;

    const version = Number.parseInt(match[1], 10);
    return Number.isNaN(version) ? null : version;
  }

  private getBottomThreshold(): number {
    const fallbackThreshold = 102;

    // Most reliable in browser/WebView: detect visible viewport loss at bottom.
    const vv = window.visualViewport;
    if (vv) {
      const bottomInset = Math.max(
        0,
        Math.round(window.innerHeight - vv.height - vv.offsetTop)
      );

      if (bottomInset > 0) {
        // Add a small breathing space above system UI.
        return Math.max(fallbackThreshold, bottomInset + 16);
      }
    }

    // Secondary heuristic: Android version from UA.
    const androidMajor = this.getAndroidMajorVersion();
    if (androidMajor === null) return fallbackThreshold;

    if (androidMajor <= 13) return 55;
    if (androidMajor >= 13) return 108;
    return fallbackThreshold;
  }

}